import { useEffect, useRef } from 'react';
import { useDesignStore } from '../store/designStore';
import { generateUuidV4 } from '../lib/guestSession';
import { safeGetItem } from '../lib/safeStorage';
import {
  getRequestedColorVariantNameFromSearch,
  getRequestedPrintMethodFromSearch,
  getRequestedPrintPositionsFromSearch,
  getRequestedPrintMethodByPositionFromSearch,
  getRequestedPrintColorByPositionFromSearch,
} from './useProductSetup';

const API_BASE = '/api';

function authHeaders(): Record<string, string> {
  const token = safeGetItem('session', 'customedge_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// "Design WIP" — every product opened in the editor gets a stable, unique id
// the moment it opens, visible in the URL as `?dwip={uuid}` and listed in the
// admin Design WIP grid (admin/src/pages/DesignWip.tsx) from that instant, not
// just once useAutoSave.ts's first 30s tick lands. Re-opening the same
// `?dwip=` link (e.g. after closing the tab and coming back a day later)
// resumes editing that exact row instead of starting a new one.
//
// Two mutually exclusive paths, split on whether `?dwip=` is already present:
//   - Absent (fresh "open the editor for any product" entry): mint a uuid,
//     create the placeholder row, then rewrite the URL to include it — so a
//     page refresh a moment later resumes the same row instead of minting a
//     second one.
//   - Present (resume link, e.g. shared or reopened from the admin grid):
//     load that design's saved canvas content once the canvas has mounted —
//     mirrors useSharedDesignLoader.ts's own `?design=` loader, including its
//     two-phase split (color variant/print method first, jsonObjects after
//     any resulting remount) — see that hook's file header for the full
//     PX113334 rationale.
export function useDesignWip() {
  const {
    canvasInstances, activeSideKey, productConfig, loadFromTemplate, savedDesignId, setSavedDesignId,
    setSelectedVariantOptions, setActiveColorVariant, setSelectedPrintMethod, setSelectedPrintColorOption,
    setSelectedPrintPositions, setSelectedPrintMethodByPosition, setSelectedPrintColorByPosition,
    setIsRestoringSavedDesign,
  } = useDesignStore();
  const attempted = useRef(false);
  const detailRef = useRef<any>(null);
  const jsonApplied = useRef(false);

  // Phase 1: fetch the design once the first canvas mounts, and apply every
  // selection that can change *which* sides/canvases exist (color variant,
  // print method/color) — but not jsonObjects yet, since the canvas(es) it
  // targets may not exist under their final keys until after this applies.
  // Mirrors useSharedDesignLoader.ts's own two-phase split and its rationale
  // (PX113334: a product with zero base imageSides stays on whatever variant
  // useProductSetup auto-selected first until colorVariantId is restored).
  useEffect(() => {
    if (attempted.current) return;
    if (!productConfig) return; // wait for product config to resolve productId

    const params = new URLSearchParams(window.location.search);
    const existingDwip = params.get('dwip');

    // `?design=` (useSharedDesignLoader.ts) is an explicit share-link open —
    // takes priority and this hook stays out of the way entirely, rather than
    // minting a stray WIP row for a design the customer is only viewing.
    if (!existingDwip && params.get('design')) { attempted.current = true; return; }

    if (existingDwip) {
      // Resume path — needs a live canvas to load Fabric JSON into, same
      // precondition as useSharedDesignLoader.ts.
      if (!canvasInstances.get(activeSideKey)) return;
      attempted.current = true;
      // Held true until Phase 2 (below) applies jsonObjects or determines
      // there's nothing to load — see designStore.ts's field comment for why
      // useFabricCanvas.ts's color-count-switch effect needs to stay quiet
      // for the whole restore sequence, not just this fetch.
      setIsRestoringSavedDesign(true);

      // Fresh-URL-wins guard — same rationale/fix as useSharedDesignLoader.ts's
      // identical block (see that hook's own comment for the full incident):
      // a resume link can carry BOTH `?dwip=<id>` (continue this exact pending
      // design) AND the customer's freshly re-picked `?colorVariant=`/
      // `?printPositions=`/etc. in the same URL. Without this guard, the
      // restore below always overwrote those fresh picks with the design's
      // OLD saved selection, regardless of which was actually more recent.
      const search = window.location.search;
      const hasFreshColorVariant = !!getRequestedColorVariantNameFromSearch(search);
      const hasFreshPrintMethod = !!getRequestedPrintMethodFromSearch(search);
      const hasFreshPositions = getRequestedPrintPositionsFromSearch(search).length > 0;
      const hasFreshMethodByPosition = Object.keys(getRequestedPrintMethodByPositionFromSearch(search)).length > 0;
      const hasFreshColorByPosition = Object.keys(getRequestedPrintColorByPositionFromSearch(search)).length > 0;

      fetch(`${API_BASE}/designs/${existingDwip}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(detail => {
          setSavedDesignId(existingDwip);
          detailRef.current = detail;

          let productInfo: any = {};
          try { productInfo = JSON.parse(detail.productInfo ?? '{}'); } catch { /* malformed — keep defaults */ }

          if (productInfo.selectedVariantOptions && Object.keys(productInfo.selectedVariantOptions).length) {
            setSelectedVariantOptions(productInfo.selectedVariantOptions);
          }

          // Restore the vendor Product Color Variant pick — without this, a
          // product with no base imageSides stays on whatever variant
          // useProductSetup auto-selected first (colorVariants[0]), showing
          // that variant's image instead of the one the customer actually
          // had selected when the design was saved. Skipped when the URL
          // already carries a fresh `?colorVariant=` — see guard above.
          if (!hasFreshColorVariant && productInfo.colorVariantId) setActiveColorVariant(productInfo.colorVariantId, true);

          try {
            const pm = detail.printingMethodData ? JSON.parse(detail.printingMethodData) : null;
            if (!hasFreshPrintMethod && pm?.method) setSelectedPrintMethod(pm.method);
            if (pm?.printColorOption != null) setSelectedPrintColorOption(pm.printColorOption);
            // positions/methodByPosition/colorByPosition — the multiselect
            // print-position feature's own per-position resolution (see
            // DesignerHeader.tsx's save payload, the source of this same
            // shape). Without restoring these on a `?dwip=` resume,
            // useProductSetup.ts's own position filter (`byPositionParam`)
            // never engages — `selectedPrintPositions` stays the store's
            // default empty array, which that filter treats as "no position
            // narrowing, show every side" — so a resumed design silently
            // showed every side/print-method on the product in both the 2D
            // Preview tab and the live canvas's side tabs, not just the
            // positions the customer actually selected before saving.
            // Reported live as "2D Preview... show only the selected
            // position images not all" on a `?dwip=` resume link. Each one
            // individually skipped when the URL already brought its own
            // fresh value — see guard above.
            if (!hasFreshPositions && pm?.positions?.length) setSelectedPrintPositions(pm.positions);
            if (!hasFreshMethodByPosition && pm?.methodByPosition && Object.keys(pm.methodByPosition).length) {
              setSelectedPrintMethodByPosition(pm.methodByPosition);
            }
            if (!hasFreshColorByPosition && pm?.colorByPosition && Object.keys(pm.colorByPosition).length) {
              setSelectedPrintColorByPosition(pm.colorByPosition);
            }
          } catch { /* malformed printingMethodData — leave unset */ }
        })
        .catch(() => {
          // Unknown/expired dwip id — fall back to editing as a fresh design
          // under that same id isn't safe (POST /designs/wip is the only
          // creator), so just proceed with a blank canvas; the next autosave
          // tick will 404 silently and the customer can still Save manually.
          setIsRestoringSavedDesign(false); // Phase 2 will never get a detail to apply
        });
      return;
    }

    // Mint path — fires once, before any canvas mount is needed.
    attempted.current = true;
    const wipId = generateUuidV4();

    fetch(`${API_BASE}/designs/wip`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        wipId,
        product_id: productConfig.productId,
        sessionId:  safeGetItem('session', 'customedge_design_hash') ?? undefined,
      }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const id = data.designId ?? wipId;
        // Read live store state, not the `savedDesignId` this closure captured
        // at mount — a manual Save/Add-to-Cart can race ahead of this POST
        // (it's usually the fastest request on the page, but not guaranteed)
        // and already have claimed a different id via its own
        // `prisma.design.create`. Blindly overwriting here would silently
        // orphan that real save: every later autosave/save would redirect to
        // this WIP row instead, leaving the customer's explicit save as an
        // invisible duplicate no UI ever points at again.
        if (useDesignStore.getState().savedDesignId) return;
        setSavedDesignId(id);
        const params = new URLSearchParams(window.location.search);
        params.set('dwip', id);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
      })
      .catch(() => {
        // Placeholder creation failed (offline, transient 5xx) — non-fatal:
        // useAutoSave.ts's first 30s tick still creates a Design row the
        // normal way, just without the up-front `?dwip=` URL / immediate
        // admin-grid visibility this hook exists to add.
      });
  }, [
    productConfig, canvasInstances, activeSideKey, setSavedDesignId, setSelectedVariantOptions,
    setActiveColorVariant, setSelectedPrintMethod, setSelectedPrintColorOption, savedDesignId,
    setSelectedPrintPositions, setSelectedPrintMethodByPosition, setSelectedPrintColorByPosition,
    setIsRestoringSavedDesign,
  ]);

  // Phase 2: once Phase 1's fetch has landed AND a canvas exists again after
  // any colorVariant-driven remount (setActiveColorVariant clears
  // canvasInstances), apply the saved canvas JSON. Guarded by its own ref so
  // it fires exactly once even though canvasInstances changes multiple times
  // in between (initial mount, then the post-remount mount) — same pattern
  // as useSharedDesignLoader.ts's Phase 2.
  useEffect(() => {
    if (jsonApplied.current) return;
    const detail = detailRef.current;
    if (!detail) return; // Phase 1's fetch hasn't landed yet — isRestoringSavedDesign stays true
    if (!detail.jsonObjects || !Object.keys(detail.jsonObjects).length) {
      // Freshly-created placeholder, nothing to load — but Phase 1 DID land
      // (detail exists), so the restore sequence is genuinely over here.
      jsonApplied.current = true;
      setIsRestoringSavedDesign(false);
      return;
    }
    if (!canvasInstances.get(activeSideKey)) return; // wait for the (possibly remounted) canvas
    jsonApplied.current = true;
    loadFromTemplate(detail.jsonObjects);
    setIsRestoringSavedDesign(false);
  }, [canvasInstances, activeSideKey, loadFromTemplate, setIsRestoringSavedDesign]);
}
